import React, { useMemo, useRef, useEffect } from "react";

import { TemplateQuestionFragment } from "../../../graphql/fragments";
import { ReportAnswer } from "../../../graphql/types";

import useTranslations from "../../../hooks/useTranslations";

import { Input, FormEntry } from "../../wombat-ui";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";
import Answer from "./utils/Answer";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";

type TemplateQuestionTextShortValue = string;

type TemplateQuestionTextShort = {
  defaultValue: TemplateQuestionTextShortValue;
} & TemplateQuestionFragment;

export function isTemplateQuestionTextShort(
  q: TemplateQuestionFragment
): q is TemplateQuestionTextShort {
  return q.type === "TemplateQuestionTextShort";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionTextShort {
  return question as TemplateQuestionTextShort;
}

type ReportAnswerTextShort = {
  value: TemplateQuestionTextShortValue;
} & ReportAnswer;

export function parseAnswers(answers: ReportAnswer[]): ReportAnswerTextShort[] {
  return answers as ReportAnswerTextShort[];
}

type TemplateQuestionTextShortProps = TemplateQuestionImplProps;

export const TemplateQuestionTextShort: React.FC<TemplateQuestionTextShortProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    status,
    errors,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );

    const inputRef = useRef(null);
    useEffect(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [inputRef.current, autoFocus]);

    return (
      <div>
        <FormEntry
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
              description={question.description}
              referents={question.referents}
            />
          }
          description={t("reports.textShortQuestion") as string}
        >
          {needsToBeFilled && (
            <Input
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
              }
              label={question.label}
              id={question.id}
              type="text"
              error={errors?.value}
              name={`${name}.value`}
              placeholder={t("reports.textShortPlaceholder") as string}
              ref={(ref) => {
                inputRef.current = ref;
                register(ref);
              }}
              srOnlyLabel
            />
          )}
        </FormEntry>
        {question.timeTracked && (
          <div className="pt-2">
            <Input
              label={
                !question.expectedTimeValue
                  ? (t("reports.timeSpentInHour") as string)
                  : `${t("reports.timeSpentInHour") as string} - ${t(
                      `reports.expectedTime.${question.expectedTimeUnit}`,
                      {
                        count: question.expectedTimeValue,
                      }
                    )}`
              }
              type="number"
              size="small"
              id={`${question.id}-timeTracked`}
              name={`${name}.timeSpent`}
              ref={register({
                valueAsNumber: true,
              })}
              disabled={!needsToBeFilled}
              defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer answer={a} answerAs={a.value} key={a.id} />
              ))}
            </div>
          </div>
        )}
        {status !== "to_be_filled_evaluator" && answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer answer={a} answerAs={a.value} key={a.id} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionTextShortPreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionTextShortPreview: React.FC<TemplateQuestionTextShortPreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <FormEntry
          title={
            <TemplateQuestionTitle
              displayIndex={displayIndex}
              title={question.label}
            />
          }
          description={t("reports.textShortQuestion") as string}
        >
          <Input label={question.label} id={question.id} disabled srOnlyLabel />
        </FormEntry>
      </div>
    );
  }
);
